import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { cloneDeep, get, isArray, orderBy, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateForm from '../../../components/ui-component/form-component/create-form/CreateForm';
import LoadingBackdrop from '../../../components/ui-component/loading/LoadingBackdrop';
import MUIAlert from '../../../components/ui-component/mui-alert/MUIAlert';
import { useAuthentication } from '../../../context/AuthContext';
import { DuplicateAntrag, getAntragTitel, getHalbJahrDetail } from '../../../lib/api/antrag';
import GetRecords from '../../../lib/api/GetRecords';
import POP_LEVEL from '../../../lib/api/Populations.json';
import { APIS } from '../../../lib/constants/apis';
import { ANTRAG_HALBJAHR_ART, ANTRAGS_STATUS, FORM_FIELDS_TYPES } from '../../../lib/constants/constant';
import { PAGES_URLS } from '../../../lib/constants/routes';
import { FormStrucItem } from '../../../lib/types/types';
import { getFieldNamefromDataPath, removeDataKeys, removeNullDeep } from '../../../utils/helper';

// import Schemas
import GetRecord from '../../../lib/api/GetRecord';
import { PostRecord } from '../../../lib/api/PostRecord';
import AntragSchema from '../../../lib/paths/antrag_FULL_Paths.json';
import SchulJahrSchema from '../../../lib/paths/schluljahr_FULL_Paths.json';
import OrganisationSchema from '../../../lib/paths/unternehmen_FULL_Paths.json';
let O_P = OrganisationSchema.attributes;
const P = AntragSchema.attributes;
const SJ_P = SchulJahrSchema.attributes;

const CreateAntrag: FC<{}> = () => {
	const navigate = useNavigate();
	const { authenticatedData } = useAuthentication();

	const [wsActive, setWsActive] = useState(false);
	const [wsError, setWsError] = useState(false);
	const [formErrorMessage, setFormErrorMessage] = useState('');
	const [loading, setLoading] = useState<boolean>(true);
	// const [hasActiveApplication, setHasActiveApplication] = useState(false);
	const [showWarning, setShowWarning] = useState(false);
	const [missingDocuments, setMissingDocuments] = useState<any[]>([]);
	const [allowMultipleApps, setAllowMultipleApps] = useState<any[]>([]);
	const [schulJahrRes, setSchulJahrRes] = useState(null);
	const [activeHalbJahr, setActiveHalbJahr] = useState('');
	const [activeKalenderJahr, setActiveKalenderJahr] = useState('');

	// Default values
	const [formValues, setFormValues] = useState({
		[P.Zusammenfassung_Ueberblick_Kalenderjahr.path]: '',
		[P.Zusammenfassung_Ueberblick_Halbjahr.path]: '',
		[P.Zusammenfassung_Ueberblick_Laufzeit_Von.path]: '',
		[P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path]: '',
		// [P.letzter_ZWV.path]: 'frischAntrag',
	});

	const [alleVertraege, setAllVertraege] = useState([]);

	useEffect(() => {
		const fetch = async () => {
			try {
				setWsActive(true);
				const filter = {
					[getFieldNamefromDataPath(P.MKS.path)]: get(authenticatedData, 'organisationId', -1),
					[getFieldNamefromDataPath(P.Zusammenfassung_Ueberblick_Kalenderjahr.path)]: get(schulJahrRes, SJ_P.Jahr.path),
				};
				const applicationRes = await GetRecords(APIS.Antrag, POP_LEVEL.antrag.FULL, filter);
				const allApplications = orderBy(
					removeDataKeys(removeNullDeep(get(applicationRes, 'data', []))),
					[`${P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path}`],
					['desc']
				);

				if (get(schulJahrRes, SJ_P.erste_Halbjahr.schema.attributes.Aktiv.path)) {
					setActiveHalbJahr(ANTRAG_HALBJAHR_ART.ersteHalbJahr);
				} else if (get(schulJahrRes, SJ_P.zweite_Halbjahr.schema.attributes.Aktiv.path)) {
					setActiveHalbJahr(ANTRAG_HALBJAHR_ART.zweiteHalbJahr);
				}

				if (get(schulJahrRes, SJ_P.Jahr.path)) {
					setActiveKalenderJahr(get(schulJahrRes, SJ_P.Jahr.path));
				}

				if (get(formValues, P.Zusammenfassung_Ueberblick_Halbjahr.path) !== activeHalbJahr) {
					setShowWarning(true);
				}

				if (allApplications.length > 0) {
					const currentHalbjahr = get(formValues, P.Zusammenfassung_Ueberblick_Halbjahr.path);
					const currentKalenderjahr = get(formValues, P.Zusammenfassung_Ueberblick_Kalenderjahr.path);

					// Check across all applications
					const hasMatchingActiveApp = allApplications.some((app: any) => {
						const appHalbjahr = get(app, P.Zusammenfassung_Ueberblick_Halbjahr.path);
						const appKalenderjahr = get(app, P.Zusammenfassung_Ueberblick_Kalenderjahr.path);
						const appStatus = get(app, P.Status_Status.path);

						return (
							appStatus !== ANTRAGS_STATUS.Abgelehnt && // active
							appHalbjahr === currentHalbjahr &&
							appKalenderjahr === currentKalenderjahr
						);
					});

					setShowWarning(hasMatchingActiveApp);
					// setHasActiveApplication(hasMatchingActiveApp);
				} else {
					setShowWarning(false);
					// setHasActiveApplication(false);
				}
			} catch (err: any) {
				setWsError(true);
				console.error('ERROR: Fetching Alte Verträge ', err);
			} finally {
				setWsActive(false);
			}
		};

		fetch();
	}, [schulJahrRes, get(formValues, P.letzter_ZWV.path)]);

	useEffect(() => {
		const fetchOrganisation = async () => {
			setLoading(true);
			try {
				let res = await GetRecord(APIS.Organisation, get(authenticatedData, 'organisationId'), POP_LEVEL.unternehmen.DOKUMENTE);
				setMissingDocuments(getOrganisationMissingDocuments(removeNullDeep(removeDataKeys(get(res, 'data', {})))));
			} catch (err) {
				console.error('ERROR Fetching Organisation ', err);
				setWsError(true);
			} finally {
				setLoading(false);
			}
		};
		fetchOrganisation();
	}, []);

	useEffect(() => {
		async function fetchAndSetLaufzeit() {
			const schulJahrRes = await fetchSchulJahr();
			setSchulJahrRes(schulJahrRes);
			setAllowMultipleApps(get(schulJahrRes, SJ_P.Allgemein_Parallele_Antraege.path));

			const schulJahrDetails = getHalbJahrDetail(schulJahrRes, activeHalbJahr);

			setFormValues((prev) => ({
				...prev,
				[P.Zusammenfassung_Ueberblick_Kalenderjahr.path]: activeKalenderJahr,
				[P.Zusammenfassung_Ueberblick_Laufzeit_Von.path]: get(schulJahrDetails, 'laufZeitVon', ''),
				[P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path]: get(schulJahrDetails, 'laufZeitBis', ''),
				[P.Zusammenfassung_Ueberblick_Halbjahr.path]: activeHalbJahr,
			}));
		}
		fetchAndSetLaufzeit();
		if (activeHalbJahr) {
			handleUpdateFormValues(P.letzter_ZWV.path, formValues[P.letzter_ZWV.path] ?? 'frischAntrag');
		}
	}, [activeHalbJahr]);

	// select existing vertrag if exists else frischAntrag
	useEffect(() => {
		if (alleVertraege && alleVertraege.length > 0) {
			setFormValues((prev) => ({
				...prev,
				[P.letzter_ZWV.path]: get(alleVertraege[0], 'id'),
			}));
		} else {
			setFormValues((prev) => ({
				...prev,
				[P.letzter_ZWV.path]: 'frischAntrag',
			}));
		}
	}, [alleVertraege]);

	const handleUpdateFormValues = async (path: string, value: any) => {
		let formValueCopy = cloneDeep(formValues);
		set(formValueCopy, path, value);
		const schulJahrDetails = getHalbJahrDetail(schulJahrRes, activeHalbJahr);
		set(formValueCopy, P.Zusammenfassung_Ueberblick_Laufzeit_Von.path, get(schulJahrDetails, 'laufZeitVon'));
		set(formValueCopy, P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path, get(schulJahrDetails, 'laufZeitBis'));

		setFormValues(formValueCopy);
		setFormErrorMessage('');
	};

	const handleCreateAntrag = async () => {
		try {
			setWsActive(true);

			// const schulJahrRes = await fetchSchulJahr(get(formValues, P.Zusammenfassung_Ueberblick_Kalenderjahr.path));
			if (!schulJahrRes) {
				setFormErrorMessage('Es wurde keine Schuljahr Information gefunden. Bitte kontaktieren Sie den Systemadministrator');
				return;
			} else {
				const schulJahrDetails = getHalbJahrDetail(schulJahrRes, activeHalbJahr);

				if (get(formValues, P.letzter_ZWV.path) !== 'frischAntrag') {
					// Dupllicate Antrag
					const letzteVertragDetails = alleVertraege.find((ele: any) => get(ele, 'id') === get(formValues, P.letzter_ZWV.path, -1));
					const MKS_person = get(authenticatedData, 'personId', -1);					
					let res = await DuplicateAntrag(get(formValues, P.letzter_ZWV.path, -1), letzteVertragDetails, schulJahrRes, MKS_person);					
					if (res) {
						navigate(PAGES_URLS.OnlineAntrag, {
							state: {
								id: res.data.id,
								api: APIS.Antrag,
							},
						});
					}
				} else {
					// Create neuen frischen Antrag
					const antragsData = {
						[getFieldNamefromDataPath(P.Zusammenfassung_Ueberblick_Titel.path)]: getAntragTitel(
							get(formValues, P.Zusammenfassung_Ueberblick_Kalenderjahr.path),
							get(formValues, P.Zusammenfassung_Ueberblick_Halbjahr.path)
						),
						[getFieldNamefromDataPath(P.Status_Status.path)]: ANTRAGS_STATUS.inBearbeitung,
						[getFieldNamefromDataPath(P.MKS.path)]: get(authenticatedData, 'organisationId', -1),
						[getFieldNamefromDataPath(P.Ansprechpartner_MKS.path)]: get(authenticatedData, 'personId', -1),
						//
						[getFieldNamefromDataPath(P.Zusammenfassung_Ueberblick_Halbjahr.path)]: get(formValues, P.Zusammenfassung_Ueberblick_Halbjahr.path),
						[getFieldNamefromDataPath(P.Zusammenfassung_Ueberblick_Kalenderjahr.path)]: get(
							formValues,
							P.Zusammenfassung_Ueberblick_Kalenderjahr.path
						),
						[getFieldNamefromDataPath(P.Zusammenfassung_Ueberblick_Laufzeit_Von.path)]: new Date(get(schulJahrDetails, 'laufZeitVon')),
						[getFieldNamefromDataPath(P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path)]: new Date(get(schulJahrDetails, 'laufZeitBis')),
					};

					let neueAntragRes = await PostRecord(APIS.Antrag, { data: antragsData });
					if (neueAntragRes) {
						navigate(PAGES_URLS.OnlineAntrag, {
							state: {
								id: get(neueAntragRes, 'data.id', -1),
								api: APIS.Antrag,
							},
						});
					}
				}
			}
		} catch (err: any) {
			console.error('ERROR: name ', err);
		} finally {
			setWsActive(false);
		}
	};

	useEffect(() => {
		const fetch = async () => {
			try {
				setWsActive(true);
				const filter = {
					[getFieldNamefromDataPath(P.MKS.path)]: get(authenticatedData, 'organisationId', -1),
					[getFieldNamefromDataPath(P.Status_Status.path)]: [ANTRAGS_STATUS.InFoerderung, ANTRAGS_STATUS.ZuwendungsvertragUnterzeichnet],
				};
				const vertragRes = await GetRecords(APIS.Antrag, POP_LEVEL.antrag.FULL, filter);
				const alleVertraege = orderBy(
					removeDataKeys(removeNullDeep(get(vertragRes, 'data', []))),
					[`${P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path}`],
					['desc']
				);
				setAllVertraege(alleVertraege);
			} catch (err: any) {
				setWsError(true);
				console.error('ERROR: Fetching Alte Verträge ', err);
			} finally {
				setWsActive(false);
			}
		};

		fetch();
	}, [authenticatedData]);

	if (wsError) {
		return <MUIAlert infoType={'error'} message='Etwas ist schief gelaufen' />;
	}
	if (!loading && missingDocuments.length > 0) {
		return (
			<>
				<LoadingBackdrop open={wsActive} />

				<Container>
					<Typography variant='h3' component={'h2'}>
						{' '}
						Antrag erstellen
					</Typography>

					<Typography>
						Folgende Dokumente sind erforderlich, bevor Sie einen Antrag stellen können
						<br />
					</Typography>
					<ul>
						{missingDocuments.map((doc, idx) => (
							<li key={idx}>{doc}</li>
						))}
					</ul>
					<Stack sx={{ mt: '3rem' }} direction={{ xs: 'column', sm: 'row' }} justifyContent={'space-between'}>
						<Button startIcon={<ArrowBack />} variant={'outlined'} onClick={() => navigate(-1)}>
							Zurück
						</Button>
						<Button onClick={() => navigate(PAGES_URLS.MeineOrganisation + '?tab=1')}>Dokumente hochladen</Button>
					</Stack>
				</Container>
			</>
		);
	}

	return (
		<>
			<LoadingBackdrop open={wsActive} />

			<Container>
				<Typography variant='h3' component={'h2'}>
					{' '}
					Antrag erstellen
				</Typography>

				<Typography>
					Hier können Sie einen neuen Antrag erstellen oder auch die Daten aus einem bestehenden Vertrag übernehmen.
					<br />
					Um die Daten aus einem bestehenden Vertrag zu übernehmen, wählen Sie zunächt den Vertrag.
				</Typography>

				{/* <MUIAlert message={'**Hinweis:** Wenn Sie nichts auswählen, wird ein neuer Antrag erstellt'} /> */}

				{formErrorMessage && (
					<Box sx={{ mt: '2rem' }}>
						<MUIAlert infoType={'error'} title='Antrag kann nicht erstellt werden' message={formErrorMessage} />{' '}
					</Box>
				)}

				<CreateForm
					formStruc={formStrucLetzter_ZWV(alleVertraege)}
					formValues={formValues}
					updateFormValuesFunc={handleUpdateFormValues}
					api={APIS.Antrag}
				/>

				{!allowMultipleApps && showWarning && (
					<Typography>
						<br />
						Es existiert schon ein Antrag für dieses Schuljahr.
					</Typography>
				)}

				{(allowMultipleApps || !showWarning) && (
					<>
						<Typography>
							<br />
							Antrag stellen für den Förderzeitraum:
						</Typography>
						<CreateForm
							formStruc={formStrucYearDetails()}
							formValues={formValues}
							updateFormValuesFunc={handleUpdateFormValues}
							api={APIS.Antrag}
						/>

						<Stack sx={{ mt: '3rem' }} direction={{ xs: 'column', sm: 'row' }} justifyContent={'space-between'}>
							<Button startIcon={<ArrowBack />} variant={'outlined'} onClick={() => navigate(PAGES_URLS.MeinBereich)}>
								Zurück
							</Button>
							<Button onClick={handleCreateAntrag} disabled={!!formErrorMessage}>
								Antrag erstellen
							</Button>
						</Stack>
					</>
				)}
			</Container>
		</>
	);
};
export default CreateAntrag;

const formStrucLetzter_ZWV = (alleVertraege: any[]) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.letzter_ZWV,
					label: 'Daten aus einem bestehenden Vertrag übernehmen',
					placeholder: 'Wählen Sie hier einem bestehenden Vertrag',
					type: FORM_FIELDS_TYPES.singleSelect,
					options: getCustomOptions(alleVertraege),
				},
			],
			userProps: { direction: 'column' },
		},
	];
	return struc;
};
const formStrucYearDetails = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ ...P.Zusammenfassung_Ueberblick_Kalenderjahr, disabled: true },
				{ ...P.Zusammenfassung_Ueberblick_Halbjahr, disabled: true },
			],
			userProps: { columnToDivide: 4 },
		},
		{
			fields: [
				{ ...P.Zusammenfassung_Ueberblick_Laufzeit_Von, disabled: true },
				{ ...P.Zusammenfassung_Ueberblick_Laufzeit_Bis, disabled: true },
			],
			userProps: { columnToDivide: 4 },
		},
	];
	return struc;
};

const fetchSchulJahr = async () => {
	try {
		const schulJahrRes = await GetRecords(APIS.SchulJahr, POP_LEVEL.schluljahr.FULL);
		if (get(schulJahrRes, 'data', []).length > 0) {
			const allschulJahre = removeNullDeep(removeDataKeys(get(schulJahrRes, 'data', [])));
			const activeSchuljahre = allschulJahre.filter((item: any) => {
				const attrs = item.attributes || {};
				return attrs.erste_Halbjahr?.Aktiv === true || attrs.zweite_Halbjahr?.Aktiv === true;
			});
			return activeSchuljahre[0];
		} else {
			return null;
		}
	} catch (err: any) {
		console.error('ERROR: Fetching SchulJahr ', err);
	}
};

const getCustomOptions = (alleVertraege: any[]) => {
	let options = [];
	if (isArray(alleVertraege) && alleVertraege.length > 0) {
		alleVertraege.forEach((ele: any) => {
			if (get(ele, P.Zusammenfassung_Ueberblick_Kalenderjahr.path) && get(ele, P.Zusammenfassung_Ueberblick_Halbjahr.path)) {
				options.push({
					value: get(ele, 'id'),
					label: getAntragTitel(get(ele, P.Zusammenfassung_Ueberblick_Kalenderjahr.path), get(ele, P.Zusammenfassung_Ueberblick_Halbjahr.path)),
				});
			} else {
				const laufZeitBisDatum = new Date(get(ele, P.Zusammenfassung_Ueberblick_Laufzeit_Bis.path));
				const jahr = laufZeitBisDatum.getFullYear();
				const monat = laufZeitBisDatum.getMonth() + 1; // getMonth() gibt 0-11 zurück, daher +1
				const halbJahrString = monat >= 1 && monat <= 6 ? ANTRAG_HALBJAHR_ART.ersteHalbJahr : ANTRAG_HALBJAHR_ART.zweiteHalbJahr;

				options.push({
					value: get(ele, 'id'),
					label: getAntragTitel(jahr, halbJahrString),
				});
			}
		});
	} else {
		options.push({ value: 'frischAntrag', label: 'mit einem frischen Antrag anfangen' });
	}

	return options;
};

const getOrganisationMissingDocuments = (organisationData: any) => {
	const missingDocuments = [];
	if (!get(organisationData, O_P.Geschaeftsbezogene_Daten_0074345863_ikschule_Im_Land_Brandenburg.path)) {
		missingDocuments.push('Bescheid über die Berechtigung zum Führen der Bezeichnung "Anerkannte Musik- und Kunstschule im Land Brandenburg"');
	}
	if (!get(organisationData, O_P.Geschaeftsbezogene_Daten_0009672916_Ueber_Die_Gemeinnuetzigkeit.path)) {
		missingDocuments.push('Bescheinigung über die Gemeinnützigkeit');
	}
	if (!get(organisationData, O_P.Geschaeftsbezogene_Daten_Satzung.path)) {
		missingDocuments.push('Satzung');
	}
	if (!get(organisationData, O_P.Geschaeftsbezogene_Daten_0201658163_register_Handelsregister_Etc.path)) {
		missingDocuments.push('Eintragungsnachweis (Vereinsregister, Handelsregister etc.)');
	}
	return missingDocuments;
};
